"use client";

import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { defaultTo, get } from "lodash-es";
import { useEffect, useMemo, useRef } from "react";
import { useLocale, useTranslations } from "use-intl";

import { AlbumGrid, type AlbumGridAlbum } from "@/components/album-grid";
import {
  MicrophoneIcon,
  MoreIcon,
  MusicIcon,
  MusicLibraryIcon,
  ReleaseIcon,
  VideoIcon,
} from "@/components/appIcons";
import type { AppIcon } from "@/components/appIcons";
import { DiscographyEmptyState } from "@/components/discography-empty-state";
import { DiscographyTypeHeader } from "@/components/discography-type-header";
import { FacetFilterBar } from "@/components/facets/facet-filter-bar";
import { handoffLinkTarget } from "@/components/handoff-link-target";
import type { LinkTarget } from "@/components/link";
import { ARTIST_DISCOGRAPHY_SURFACE } from "@/facets/config";
import type { FacetFiltersController } from "@/facets/use-facet-filters";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useListingCounts } from "@/hooks/use-listing-counts";
import { useReleaseGroupFilterOptions } from "@/hooks/use-release-group-filter-options";
import { useTopLoaderOnRefetch } from "@/hooks/use-top-loader-on-refetch";
import {
  GROUPED_DISCOGRAPHY_WORK_HANDOFF_EXCLUDED_KEYS,
  getWorkReleaseHandoffSearch,
} from "@/lib/faceted-search-url";
import type { DiscographyQueryType } from "@/types/discography";
import { type AlbumType } from "@garimpa/lib/albumTypes";
import {
  DISCOGRAPHY_SORTS,
  type DiscographySort as SortOption,
} from "@garimpa/lib/discographySorts";
import { type FormatCategoryWithOther as FormatCategory } from "@garimpa/lib/formatGroups";

import { getArtistAlbumsInfiniteQueryOptions } from "./artist-albums-query";
import { getArtistDiscographyCountsQueryOptions } from "./artist-discography-counts-query";
import { resolveDiscographyViewState } from "./discography-view-state";

export type { SortOption, AlbumType };
export type FormatFilter = FormatCategory | "all";
export type TypeFilter = AlbumType | "all";

export const ALBUM_TYPE_OPTIONS: { key: AlbumType; labelKey: string; icon: AppIcon }[] = [
  { key: "album", labelKey: "tabAlbums", icon: ReleaseIcon },
  { key: "compilation", labelKey: "tabCompilations", icon: MusicLibraryIcon },
  { key: "single_ep", labelKey: "tabSinglesEPs", icon: MusicIcon },
  { key: "live", labelKey: "tabLive", icon: MicrophoneIcon },
  { key: "video", labelKey: "tabVideo", icon: VideoIcon },
  { key: "other", labelKey: "tabOthers", icon: MoreIcon },
];

export const SORT_OPTIONS = DISCOGRAPHY_SORTS;
export const DEFAULT_SORT: SortOption = "oldest";

const EMPTY_TYPE_COUNTS: Record<AlbumType | "all", number> = {
  all: 0,
  album: 0,
  compilation: 0,
  single_ep: 0,
  live: 0,
  video: 0,
  other: 0,
} as const;

interface ArtistDiscographyProps {
  artistId: string;
  discographyQueryType: DiscographyQueryType;
  format: FormatFilter;
  sort: SortOption;
  facetControls: FacetFiltersController;
  searchValue: string;
  onSearchChange: (value: string) => void;
  updateFilter: (key: string, value: string | undefined) => void;
  getTypeLinkTarget?: (value: TypeFilter) => LinkTarget;
  getFormatLinkTarget?: (value: string) => LinkTarget;
}

export function ArtistDiscography({
  artistId,
  discographyQueryType,
  format,
  sort,
  facetControls,
  searchValue,
  onSearchChange,
  updateFilter,
  getTypeLinkTarget,
  getFormatLinkTarget,
}: ArtistDiscographyProps) {
  const t = useTranslations("artist");
  const locale = useLocale();
  const sentinelRef = useRef<HTMLDivElement>(null);

  const debouncedSearch = useDebouncedValue(searchValue, 300);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isFetching } =
    useInfiniteQuery({
      ...getArtistAlbumsInfiniteQueryOptions({
        artistId,
        discographyQueryType,
        filter: facetControls.filter,
        format,
        sort,
        search: debouncedSearch,
      }),
      placeholderData: keepPreviousData,
      select: (data) => ({
        items: data.pages.flatMap((page) => page.items),
        effectiveType: (data.pages[0]?.effectiveType ?? "all") as TypeFilter,
        facets: data.pages[0]?.facets ?? null,
      }),
    });
  useTopLoaderOnRefetch([isFetching, !isFetchingNextPage, !isLoading].every(Boolean));

  const effectiveType = get(data, "effectiveType") as TypeFilter | undefined;
  const albums = defaultTo(get(data, "items"), []);

  // Static type-tab counts (format/search-independent). Prefetched on the
  // server in page.tsx and hydrated here — shares the queryOptions shape so
  // the client key matches the dehydrated state and the badges render SSR.
  const { data: typeCountsData } = useQuery({
    ...getArtistDiscographyCountsQueryOptions(artistId),
    placeholderData: keepPreviousData,
  });
  const typeCounts = defaultTo(typeCountsData, EMPTY_TYPE_COUNTS);
  const facetOptions = useReleaseGroupFilterOptions(get(data, "facets"), locale);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: "0px 0px 400px 0px" },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const workIds = useMemo(() => albums.map((a: AlbumGridAlbum) => a.id), [albums]);
  const { listingMap } = useListingCounts({ workIds });

  const { emptyReason, hasActiveFilters, isTypeResolved, selectedType } =
    resolveDiscographyViewState({
      discographyQueryType,
      effectiveType,
      typeCounts,
      typeCountsLoaded: typeCountsData !== undefined,
      albumCount: albums.length,
      isLoading,
      hasFacetFilters: facetControls.hasActive,
      searchValue,
    });

  if (emptyReason === "empty") {
    return (
      <section>
        <DiscographyEmptyState
          icon={MusicLibraryIcon}
          title={t("noDiscographyTitle")}
          description={t("noDiscographyDescription")}
        />
      </section>
    );
  }

  const handleClearNarrowingFilters = () => {
    facetControls.clearAll();
    onSearchChange("");
  };

  return (
    <section>
      <DiscographyTypeHeader
        title={t("discography")}
        allLabel={t("tabAll")}
        options={ALBUM_TYPE_OPTIONS.map((option) => ({
          key: option.key,
          label: t(option.labelKey),
          icon: option.icon,
        }))}
        counts={typeCounts}
        selectedType={selectedType}
        showTabs={isTypeResolved}
        onTypeChange={(type) => updateFilter("type", type)}
        getTypeLinkTarget={getTypeLinkTarget && ((type) => getTypeLinkTarget(type as TypeFilter))}
      />

      {[hasActiveFilters, defaultTo(get(data, "facets.totalCount"), 0) > 0].some(Boolean) && (
        <FacetFilterBar
          surface={ARTIST_DISCOGRAPHY_SURFACE}
          options={facetOptions}
          controller={facetControls}
          search={searchValue}
          onSearchChange={(value) => onSearchChange(value ?? "")}
          searchPlaceholder={t("searchPlaceholder")}
          formatLinkTarget={getFormatLinkTarget}
          layout="compact"
        />
      )}

      <AlbumGrid
        albums={albums}
        subtitle={(album: AlbumGridAlbum) => (album.year ? String(album.year) : undefined)}
        link={(album) =>
          handoffLinkTarget({
            to: "/work/$id",
            params: { id: album.id },
            search: getWorkReleaseHandoffSearch({
              excludeKeys: GROUPED_DISCOGRAPHY_WORK_HANDOFF_EXCLUDED_KEYS,
              filter: facetControls.filter,
              q: searchValue,
            }),
          })
        }
        listingMap={listingMap}
        isLoading={isLoading}
        emptyState={
          emptyReason === "filtered" ? (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <p className="text-muted-foreground">{t("noAlbumsFilters")}</p>
              <button
                type="button"
                onClick={handleClearNarrowingFilters}
                className="text-sm font-medium text-primary hover:underline"
              >
                {t("clearFilters")}
              </button>
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-12">
              {searchValue ? t("noAlbumsSearch") : t("noAlbums")}
            </p>
          )
        }
      >
        {hasNextPage && (
          <div ref={sentinelRef} className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
          </div>
        )}
      </AlbumGrid>
    </section>
  );
}
